import clsx from 'clsx'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface PageHeaderProps {
  title: string
  description?: string
  breadcrumbs?: BreadcrumbItem[]
  action?: React.ReactNode
  className?: string
}

/**
 * Consistent page header used across all app pages.
 * Shows title, optional description, breadcrumb trail, and action slot.
 */
export function PageHeader({ title, description, breadcrumbs, action, className }: PageHeaderProps) {
  return (
    <div className={clsx('flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3', className)}>
      <div className="min-w-0">
        {/* Breadcrumb */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1 mb-2">
            {breadcrumbs.map((item, i) => (
              <div key={i} className="flex items-center gap-1">
                {i > 0 && <ChevronRight size={12} className="text-slate-600" />}
                {item.href ? (
                  <Link
                    to={item.href}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-xs text-slate-400">{item.label}</span>
                )}
              </div>
            ))}
          </nav>
        )}

        {/* Title */}
        <h1 className="text-xl font-bold text-slate-100 tracking-tight">{title}</h1>

        {/* Description */}
        {description && (
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">{description}</p>
        )}
      </div>

      {/* Action slot */}
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}
